import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, Grid, MenuItem, Stack, TextField, Typography } from '@mui/material';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined';
import ScaleOutlinedIcon from '@mui/icons-material/ScaleOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { useNavigate } from 'react-router-dom';
import { AnimatedPage, AnimatedSection, metricMotion } from '../components/animations/MotionContainers';
import { DailyEvolutionCard } from '../components/dashboard/DailyEvolutionCard';
import { FlowBreakdownCard } from '../components/dashboard/FlowBreakdownCard';
import { SummaryMetricCard } from '../components/dashboard/SummaryMetricCard';
import { VarietySummaryTable } from '../components/dashboard/VarietySummaryTable';
import { EntradasTable } from '../components/entradas/EntradasTable';
import { useFlujosCatalogs } from '../hooks/useFlujosCatalogs';
import { useFlujosEntradas } from '../hooks/useFlujosEntradas';
import {
    formatDisplayDate,
    getCampaignOptionsFromEntries,
    getCurrentCampaignRange,
    getDailyEvolution,
    getLatestEntries,
} from '../utils/dashboardData';
import {
    calculateFlowTotals,
    formatKilos,
    formatPercent,
    groupTotalsByVarietyAndWarehouse,
} from '../utils/flujoCalculations';
import { isDateInRange } from '../utils/dateFormatters';
import { usuarios } from '../utils/mockFlujosData';

const warehouseColors = {
    1: '#ff7900',
    2: '#1570ef',
    3: '#079455',
};

export const Inicio = () => {
    const navigate = useNavigate();
    const {
        almacenes,
        variedades,
        cajasPalet,
        loading: catalogsLoading,
        error: catalogsError,
    } = useFlujosCatalogs();
    const { rows: entradas, loading: entradasLoading, error: entradasError } = useFlujosEntradas({
        limit: 500,
        sortBy: 'fecha',
        sortDirection: 'desc',
    }, { almacenes, variedades, cajasPalet });
    const currentCampaignStartYear = getCurrentCampaignRange().startYear;
    const campaignOptions = useMemo(
        () => getCampaignOptionsFromEntries(entradas),
        [entradas]
    );
    const [selectedCampaignStartYear, setSelectedCampaignStartYear] = useState(currentCampaignStartYear);
    const [selectedWarehouseIds, setSelectedWarehouseIds] = useState([]);
    const selectedCampaign = useMemo(
        () => campaignOptions.find((campaign) => campaign.startYear === selectedCampaignStartYear) ||
            getCurrentCampaignRange(),
        [campaignOptions, selectedCampaignStartYear]
    );
    const campaignRows = useMemo(
        () => entradas.filter((row) => (
            isDateInRange(row.fecha, selectedCampaign.startValue, selectedCampaign.endValue)
        )),
        [entradas, selectedCampaign]
    );
    const flowRows = useMemo(() => {
        if (selectedWarehouseIds.length === 0) {
            return campaignRows;
        }

        return campaignRows.filter((row) => selectedWarehouseIds.includes(row.idAlmacen));
    }, [campaignRows, selectedWarehouseIds]);
    const totals = calculateFlowTotals(flowRows);
    const varietyRows = Object.values(groupTotalsByVarietyAndWarehouse(flowRows, almacenes));
    const latestEntries = getLatestEntries(flowRows, 6);
    const allWarehousesSelected = selectedWarehouseIds.length === 0;
    const activeWarehouses = allWarehousesSelected
        ? almacenes
        : almacenes.filter((almacen) => selectedWarehouseIds.includes(almacen.id));
    const showWarehouseSegments = activeWarehouses.length > 1;
    const dailyEvolution = getDailyEvolution(
        flowRows,
        showWarehouseSegments ? activeWarehouses : [],
        warehouseColors
    );
    const getWarehouseSegments = (field) =>
        showWarehouseSegments
            ? activeWarehouses.map((almacen) => ({
                label: almacen.nombre,
                value: flowRows
                    .filter((row) => row.idAlmacen === almacen.id)
                    .reduce((acc, row) => acc + row[field], 0),
                color: warehouseColors[almacen.id],
            }))
            : undefined;
    const breakdownItems = [
        { label: 'Precalibrado', value: totals.otrosCalibres, color: '#7a5af8', segments: getWarehouseSegments('otrosCalibres') },
        { label: 'Retorno', value: totals.retorno, color: '#1570ef', segments: getWarehouseSegments('retorno') },
        { label: 'Verde', value: totals.verde, color: '#079455', segments: getWarehouseSegments('verde') },
        { label: 'Industria', value: totals.industria, color: '#a15c00', segments: getWarehouseSegments('industria') },
        { label: 'Podrido', value: totals.podrido, color: '#d92d20', segments: getWarehouseSegments('podrido') },
        { label: 'Bajo calibre', value: totals.bajoCalibre, color: '#68727d', segments: getWarehouseSegments('bajoCalibre') },
        { label: 'Segunda', value: totals.segunda, color: '#12b76a', segments: getWarehouseSegments('segunda') },
    ];

    const toggleWarehouse = (warehouseId) => {
        setSelectedWarehouseIds((current) => {
            if (current.includes(warehouseId)) {
                return current.filter((id) => id !== warehouseId);
            }

            const nextSelectedWarehouseIds = [...current, warehouseId];

            if (nextSelectedWarehouseIds.length === almacenes.length) {
                return [];
            }

            return nextSelectedWarehouseIds;
        });
    };

    return (
        <AnimatedPage>
                <Stack spacing={2.5}>
                    <AnimatedSection>
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={2}
                            sx={{ justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' } }}
                        >
                            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', minWidth: 0 }}>
                                <Box
                                    sx={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: '50%',
                                        bgcolor: 'primary.main',
                                        color: 'primary.contrastText',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flex: '0 0 auto',
                                    }}
                                >
                                    <AssessmentOutlinedIcon />
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="h1">Resumen de flujos de fruta</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Totales acumulados y ultimos registros introducidos
                                    </Typography>
                                </Box>
                            </Stack>

                            <Stack
                                direction={{ xs: 'column', lg: 'row' }}
                                spacing={1.5}
                                sx={{ alignItems: { xs: 'stretch', lg: 'center' }, minWidth: { xs: '100%', sm: 'auto' } }}
                            >
                                <Box
                                    sx={{
                                        p: 1,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                        bgcolor: 'background.paper',
                                        minWidth: { xs: '100%', lg: 260 },
                                    }}
                                >
                                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                        <CalendarMonthOutlinedIcon color="primary" fontSize="medium" />
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography sx={{ color: 'primary.main', fontSize: 12, fontWeight: 900, lineHeight: '15px' }}>
                                                Campaña
                                            </Typography>
                                            <Typography sx={{ fontSize: 11, fontWeight: 700, lineHeight: '14px' }}>
                                                {formatDisplayDate(selectedCampaign.startValue)} - {formatDisplayDate(selectedCampaign.endValue)}
                                            </Typography>
                                        </Box>
                                        <TextField
                                            select
                                            size="small"
                                            value={selectedCampaignStartYear}
                                            onChange={(event) => setSelectedCampaignStartYear(Number(event.target.value))}
                                            sx={{ minWidth: 112 }}
                                        >
                                            {campaignOptions.map((campaign) => (
                                                <MenuItem key={campaign.startYear} value={campaign.startYear}>
                                                    {campaign.label}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Stack>
                                </Box>

                                <Box
                                    sx={{
                                        p: 1,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                        bgcolor: 'background.paper',
                                        minWidth: { xs: '100%', sm: 'auto' },
                                    }}
                                >
                                <Stack
                                    direction={{ xs: 'column', md: 'row' }}
                                    spacing={1}
                                    sx={{
                                        alignItems: { xs: 'stretch', md: 'center' },
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        spacing={0.75}
                                        sx={{ alignItems: 'center', color: 'text.secondary', px: { md: 0.5 } }}
                                    >
                                        <WarehouseOutlinedIcon color="primary" fontSize="medium" />
                                        <Box>
                                            <Typography sx={{ color: 'primary.main', fontSize: 12, fontWeight: 900, lineHeight: '15px' }}>
                                                Almacenes
                                            </Typography>
                                            <Typography sx={{ fontSize: 11, fontWeight: 700, lineHeight: '14px' }}>
                                                Filtrar dashboard
                                            </Typography>
                                        </Box>
                                    </Stack>
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        sx={{
                                            flexWrap: 'wrap',
                                            justifyContent: { xs: 'flex-start', md: 'flex-end' },
                                            gap: 1,
                                        }}
                                    >
                                        <Button
                                            variant={allWarehousesSelected ? 'contained' : 'outlined'}
                                            color={allWarehousesSelected ? 'primary' : 'secondary'}
                                            size="small"
                                            onClick={() => setSelectedWarehouseIds([])}
                                            sx={{ minHeight: 34, px: 1.75, fontWeight: 900 }}
                                        >
                                            Todos
                                        </Button>
                                        {almacenes.map((almacen) => {
                                            const active = selectedWarehouseIds.includes(almacen.id);

                                            return (
                                                <Button
                                                    key={almacen.id}
                                                    variant={active ? 'contained' : 'outlined'}
                                                    color={active ? 'primary' : 'secondary'}
                                                    size="small"
                                                    onClick={() => toggleWarehouse(almacen.id)}
                                                    sx={{ minHeight: 34, px: 1.75, fontWeight: 900 }}
                                                >
                                                    {almacen.nombre}
                                                </Button>
                                            );
                                        })}
                                    </Stack>
                                </Stack>
                                </Box>
                            </Stack>
                        </Stack>
                    </AnimatedSection>

                    {(entradasLoading || catalogsLoading || entradasError || catalogsError) && (
                        <Alert severity={entradasError || catalogsError ? 'error' : 'info'} variant="outlined">
                            {entradasError || catalogsError || 'Cargando datos...'}
                        </Alert>
                    )}

                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6, lg: 2.4 }}>
                            <AnimatedSection variant={metricMotion}>
                                <SummaryMetricCard
                                    icon={<ScaleOutlinedIcon />}
                                    title="Kilos volcados"
                                    value={formatKilos(totals.kilosVolcados)}
                                    suffix="kg"
                                />
                            </AnimatedSection>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, lg: 2.4 }}>
                            <AnimatedSection variant={metricMotion}>
                                <SummaryMetricCard
                                    icon={<Inventory2OutlinedIcon />}
                                    title="Kilos clasificados"
                                    value={formatKilos(totals.kilosClasificados)}
                                    suffix="kg"
                                />
                            </AnimatedSection>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, lg: 2.4 }}>
                            <AnimatedSection variant={metricMotion}>
                                <SummaryMetricCard
                                    icon={<WarningAmberOutlinedIcon />}
                                    title="Sin clasificar"
                                    value={formatKilos(totals.kilosSinClasificar)}
                                    suffix="kg"
                                />
                            </AnimatedSection>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, lg: 2.4 }}>
                            <AnimatedSection variant={metricMotion}>
                                <SummaryMetricCard
                                    icon={<PercentOutlinedIcon />}
                                    title="Rendimiento"
                                    value={formatPercent(totals.rendimiento)}
                                    suffix="%"
                                />
                            </AnimatedSection>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, lg: 2.4 }}>
                            <AnimatedSection variant={metricMotion}>
                                <SummaryMetricCard
                                    icon={<FactCheckOutlinedIcon />}
                                    title="Registros"
                                    value={flowRows.length}
                                    helper="Muestra provisional"
                                />
                            </AnimatedSection>
                        </Grid>
                    </Grid>

                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, lg: 5 }}>
                            <AnimatedSection>
                                <FlowBreakdownCard
                                    title="Clasificacion acumulada"
                                    items={breakdownItems}
                                    total={totals.kilosClasificados}
                                />
                            </AnimatedSection>
                        </Grid>
                        <Grid size={{ xs: 12, lg: 7 }}>
                            <AnimatedSection>
                                <VarietySummaryTable rows={varietyRows} />
                            </AnimatedSection>
                        </Grid>
                    </Grid>

                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, lg: 8.5 }}>
                            <AnimatedSection>
                                <EntradasTable
                                    title="Ultimas entradas"
                                    rows={latestEntries}
                                    warehouses={almacenes}
                                    users={usuarios}
                                    onRowClick={(row) => navigate(`/consulta-entradas/${row.id || row.codigo}`)}
                                />
                            </AnimatedSection>
                        </Grid>
                        <Grid size={{ xs: 12, lg: 3.5 }}>
                            <AnimatedSection>
                                <DailyEvolutionCard rows={dailyEvolution} showSegments={showWarehouseSegments} />
                            </AnimatedSection>
                        </Grid>
                    </Grid>
                </Stack>
        </AnimatedPage>
    );
};
